import { useEffect, useRef, useState } from 'react'
import styles from './SortingFilter.module.css'

type SortOption = 'popularity' | 'price-asc' | 'price-desc' | 'newest'

interface SortingFilterProps {
  onSortChange?: (sortOption: SortOption) => void
  className?: string
  initialSort?: SortOption
}

const sortOptions = [
  { value: 'popularity', label: 'За популярністю' },
  { value: 'price-asc', label: 'Ціна за зростанням' },
  { value: 'price-desc', label: 'Ціна за спаданням' },
  { value: 'newest', label: 'За новизною' },
]

export default function SortingFilter({
  onSortChange,
  className = '',
  initialSort = 'popularity',
}: SortingFilterProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedOption, setSelectedOption] = useState<SortOption>(initialSort)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setSelectedOption(initialSort)
  }, [initialSort])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleOptionSelect = (option: SortOption) => {
    setSelectedOption(option)
    setIsOpen(false)
    onSortChange?.(option)
  }

  const selectedLabel = sortOptions.find(option => option.value === selectedOption)?.label

  return (
    <div className={`${styles.sortingFilter} ${className}`} ref={dropdownRef}>
      <button
        className={styles.sortButton}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className={styles.sortLabel}>{selectedLabel}</span>
        <svg
          className={`${styles.chevron} ${isOpen ? styles.chevronUp : ''}`}
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
        >
          <path
            d="M4 6L8 10L12 6"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <div className={styles.dropdown}>
          <ul className={styles.optionsList} role="listbox">
            {sortOptions.map(option => (
              <li key={option.value} role="option" aria-selected={selectedOption === option.value}>
                <button
                  className={`${styles.option} ${
                    selectedOption === option.value ? styles.selected : ''
                  }`}
                  onClick={() => handleOptionSelect(option.value as SortOption)}
                >
                  {option.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
